import React, { useEffect, useRef, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { Send } from "lucide-react";

function timeOf(iso) {
  try { return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function ProjektChat({ projektId, compact = false }) {
  const { user } = useAuth();
  const t = useT();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/projekte/${projektId}/chat`);
    setMessages(data);
    setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
  };

  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [projektId]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim(); if (!t) return;
    setBusy(true);
    try {
      await api.post(`/projekte/${projektId}/chat`, { text: t });
      setText("");
      await load();
    } finally { setBusy(false); }
  };

  const roleColor = { admin: "#1B1E23", pm: "#0E62C4", designer: "#BE2E6C" };

  // Group messages by date
  const grouped = [];
  let lastDate = null;
  messages.forEach((m) => {
    const d = fmtDate(m.erstellt_am);
    if (d !== lastDate) { grouped.push({ divider: d, id: `d-${d}` }); lastDate = d; }
    grouped.push(m);
  });

  return (
    <div className={`paper-card flex flex-col ${compact ? "h-[420px]" : "h-[600px]"}`} data-testid="chat-container">
      <div className="px-5 py-3 border-b border-line bg-paper/60">
        <h3 className="font-archivo font-bold text-sm">{t("chat.title")}</h3>
        <div className="mono-label mt-0.5">{messages.length === 1 ? t("chat.count_message_one") : t("chat.count_messages").replace("{n}", messages.length)}</div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-messages">
        {grouped.length === 0 && (
          <div className="h-full flex items-center justify-center text-sm text-ink/40">{t("chat.no_messages")}</div>
        )}
        {grouped.map((m) => {
          if (m.divider) return (
            <div key={m.id} className="text-center my-3">
              <span className="mono-label px-2" style={{opacity:1}}>{m.divider}</span>
            </div>
          );
          const mine = m.verfasser_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
              <div className="max-w-[75%]">
                {!mine && (
                  <div className="mono-label mb-0.5 pl-1" style={{ color: roleColor[m.verfasser_rolle] || "#1B1E23", opacity: 1 }}>
                    {m.verfasser_name} · {m.verfasser_rolle}
                  </div>
                )}
                <div className={`rounded-md px-3 py-2 text-sm whitespace-pre-wrap break-words ${mine ? "text-white" : "bg-paper border border-line"}`}
                     style={mine ? { backgroundColor: roleColor[m.verfasser_rolle] || "#1B1E23" } : {}}>
                  {m.text}
                </div>
                <div className={`mono-label mt-0.5 ${mine ? "text-right pr-1" : "pl-1"}`}>{timeOf(m.erstellt_am)}</div>
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={send} className="p-3 border-t border-line flex gap-2" data-testid="chat-form">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="chat-input"
          placeholder={t("chat.input_placeholder")}
          className="flex-1 px-3 py-2 border border-line rounded-sm text-sm bg-white focus:outline-none focus:border-ink"
        />
        <button type="submit" disabled={busy || !text.trim()} data-testid="chat-send" className="inline-flex items-center gap-1.5 px-3 py-2 bg-ink text-white rounded-sm text-sm hover:bg-black disabled:opacity-50">
          <Send size={13} /> {t("chat.send")}
        </button>
      </form>
    </div>
  );
}
